import { useState } from 'react';
import { FileRejection, FileWithPath } from 'react-dropzone';
import { Modal, Dropzone, useMutation, useLocale, DropzoneErrorCodes, ProfileField } from 'shared-ui';

import { validateLicense } from 'api/license/license';
import { AppMessages } from 'i18n/messages';
import { ACCEPTED_LICENSE_FILE_TYPES, MAX_IMPORT_FILES } from 'core/global.variables';

import { AddLicenseModalProps } from './AddLicenseModal.types';
import * as Styled from './AddLicenseModal.styles';

const MAX_LICENSE_FILE_SIZE_BYTES = 1000000;

export const AddLicenseModal = ({ isOpen, onClose, onSubmit, isSubmitting }: AddLicenseModalProps) => {
  const { formatMessage } = useLocale();
  const [file, setFile] = useState<FileWithPath | undefined>();
  const [fileError, setFileError] = useState<string | undefined>();
  const [validatedLicense, setValidatedLicense] = useState<{
    numerLicencji: string;
    nazwaLicencjobiorcy: string;
    dataWygenerowania: string;
    dataWaznosci: string;
    maxZalogowanychUzytkownikow: number;
    licenseKey: string;
  } | null>(null);

  const { mutate: validateFile, isLoading: isValidating } = useMutation(validateLicense, {
    onSuccess: (data) => {
      setValidatedLicense(data);
      setFileError(undefined);
    },
    onError: () => {
      setFileError(formatMessage({ id: AppMessages['license.modal.error.invalidLicenseKey'] }));
      setValidatedLicense(null);
    },
  });

  const handleDrop = (acceptedFiles: FileWithPath[]) => {
    if (acceptedFiles.length === 0) return;

    const newFile = acceptedFiles[0];
    setFile(newFile);
    setFileError(undefined);
    setValidatedLicense(null);

    validateFile({ file: newFile });
  };

  const handleDropRejected = (rejectedFiles: FileRejection[]) => {
    const errorCode = rejectedFiles[0]?.errors[0]?.code;

    if (errorCode === DropzoneErrorCodes.FILE_INVALID_TYPE) {
      setFileError(formatMessage({ id: AppMessages['license.modal.error.invalidFileType'] }));
    } else if (errorCode === DropzoneErrorCodes.FILE_TOO_LARGE) {
      setFileError(formatMessage({ id: AppMessages['license.modal.error.invalidFileType'] }));
    }
  };

  const handleRemoveFile = () => {
    setFile(undefined);
    setFileError(undefined);
    setValidatedLicense(null);
  };

  const handleClose = () => {
    handleRemoveFile();
    onClose();
  };

  const handleSubmit = () => {
    if (validatedLicense) {
      onSubmit(validatedLicense.licenseKey);
    }
  };

  return (
    <Modal
      header={formatMessage({ id: AppMessages['license.modal.title'] })}
      submitButtonText={formatMessage({ id: AppMessages['common.add'] })}
      onClose={handleClose}
      open={isOpen}
      size="medium"
      type="form"
      onSubmit={handleSubmit}
      submitButtonDisabled={!validatedLicense || !!fileError}
      isLoading={isSubmitting}
    >
      <Styled.ModalContent>
        <Dropzone
          file={file}
          onClickRemoveButton={handleRemoveFile}
          dropzoneDescription={formatMessage({ id: AppMessages['license.modal.dropzone.description'] })}
          isLoading={isValidating}
          error={fileError}
          addedFileSubtext={file && `${(file.size / 1000).toFixed(2)} KB`}
          rootDropzoneProps={{
            multiple: false,
            maxFiles: MAX_IMPORT_FILES,
            maxSize: MAX_LICENSE_FILE_SIZE_BYTES,
            accept: ACCEPTED_LICENSE_FILE_TYPES,
            onDrop: handleDrop,
            onDropRejected: handleDropRejected,
          }}
          title={
            <>
              {formatMessage({ id: AppMessages['license.modal.stepOne'] })}
              <Styled.SubtitleText>
                {formatMessage({ id: AppMessages['license.modal.stepOne.subtitle'] })}
              </Styled.SubtitleText>
            </>
          }
        />

        <Styled.StepTitle>
          {formatMessage({ id: AppMessages['license.modal.stepTwo'] })}
          <Styled.SubtitleText>
            {formatMessage({ id: AppMessages['license.modal.stepTwo.subtitle'] })}
          </Styled.SubtitleText>
        </Styled.StepTitle>
        <Styled.StepSection $isDisabled={!validatedLicense}>
          <Styled.PreviewFields>
            <ProfileField label={formatMessage({ id: AppMessages['license.number'] })}>
              {validatedLicense?.numerLicencji || '-'}
            </ProfileField>
            <ProfileField label={formatMessage({ id: AppMessages['license.licenseeName'] })}>
              {validatedLicense?.nazwaLicencjobiorcy || '-'}
            </ProfileField>
            <ProfileField label={formatMessage({ id: AppMessages['license.validFrom'] })}>
              {validatedLicense?.dataWygenerowania || '-'}
            </ProfileField>
            <ProfileField label={formatMessage({ id: AppMessages['license.validTo'] })}>
              {validatedLicense?.dataWaznosci || '-'}
            </ProfileField>
            <ProfileField label={formatMessage({ id: AppMessages['license.maxUsers'] })}>
              {validatedLicense?.maxZalogowanychUzytkownikow ?? '-'}
            </ProfileField>
          </Styled.PreviewFields>
        </Styled.StepSection>
      </Styled.ModalContent>
    </Modal>
  );
};
